import React from 'react';
import { X, AlertTriangle, Lock, Unlock, Trash2, Building2 } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type: 'lock' | 'unlock' | 'delete' | 'bulk-lock' | 'bulk-unlock' | 'bulk-delete' | 'remove-from-ict';
  deviceCount?: number;
  loading?: boolean;
}

export function ConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  type, 
  deviceCount = 1,
  loading = false 
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'lock':
      case 'bulk-lock':
        return <Lock className="w-6 h-6 text-white" />;
      case 'unlock':
      case 'bulk-unlock':
        return <Unlock className="w-6 h-6 text-white" />;
      case 'delete':
      case 'bulk-delete':
        return <Trash2 className="w-6 h-6 text-white" />;
      case 'remove-from-ict':
        return <Building2 className="w-6 h-6 text-white" />;
      default:
        return <AlertTriangle className="w-6 h-6 text-white" />;
    }
  };

  const getIconBg = () => {
    switch (type) {
      case 'lock':
      case 'bulk-lock':
        return 'bg-gradient-to-br from-red-500 to-red-600';
      case 'unlock':
      case 'bulk-unlock':
        return 'bg-gradient-to-br from-emerald-500 to-emerald-600';
      case 'delete':
      case 'bulk-delete':
        return 'bg-gradient-to-br from-red-500 to-red-600';
      case 'remove-from-ict':
        return 'bg-gradient-to-br from-blue-500 to-blue-600';
      default:
        return 'bg-gradient-to-br from-amber-500 to-amber-600';
    }
  };

  const getConfirmButtonStyle = () => {
    switch (type) {
      case 'lock':
      case 'bulk-lock':
        return 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800';
      case 'unlock':
      case 'bulk-unlock':
        return 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800';
      case 'delete':
      case 'bulk-delete':
        return 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800';
      case 'remove-from-ict':
        return 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800';
      default:
        return 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800';
    }
  };

  const getConfirmButtonText = () => {
    if (loading) return 'Processing...';
    
    switch (type) {
      case 'lock':
        return 'Lock Device';
      case 'bulk-lock':
        return `Lock ${deviceCount} Device${deviceCount > 1 ? 's' : ''}`;
      case 'unlock':
        return 'Unlock Device';
      case 'bulk-unlock':
        return `Unlock ${deviceCount} Device${deviceCount > 1 ? 's' : ''}`;
      case 'delete':
        return 'Delete Device';
      case 'bulk-delete':
        return `Delete ${deviceCount} Device${deviceCount > 1 ? 's' : ''}`;
      case 'remove-from-ict':
        return 'Move to Field';
      default:
        return 'Confirm';
    }
  };

  const getModalWidth = () => {
    switch (type) {
      case 'remove-from-ict':
        return 'max-w-lg'; // Larger width for ICT removal with detailed message
      default:
        return 'max-w-md';
    }
  };

  const formatMessage = () => {
    if (type === 'remove-from-ict') {
      // Split the ICT message into sections for better readability
      const parts = message.split('\n\n');
      return (
        <div className="space-y-4">
          <p className="text-slate-700 dark:text-slate-300">
            {parts[0]}
          </p>
          {parts[1] && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4">
              <h4 className="font-semibold text-red-800 dark:text-red-300 mb-2">
                The following information will be permanently deleted:
              </h4>
              <div className="text-sm text-red-700 dark:text-red-400 space-y-1">
                {parts[1].split('\n').map((line, index) => {
                  if (line.startsWith('•')) {
                    return <div key={index} className="flex items-start space-x-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>{line.substring(2)}</span>
                    </div>
                  }
                  return line ? <div key={index}>{line}</div> : null;
                })}
              </div>
            </div>
          )}
          {parts[2] && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
              <div className="text-sm text-blue-700 dark:text-blue-400">
                {parts[2].split('\n').map((line, index) => (
                  line ? <div key={index}>{line}</div> : null
                ))}
              </div>
            </div>
          )}
          {parts[3] && (
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400 text-center">
              {parts[3]}
            </p>
          )}
        </div>
      );
    }
    
    return (
      <p className="text-slate-700 dark:text-slate-300 mb-6 leading-relaxed">
        {message}
      </p>
    );
  };

  return (
    <div className="modal-overlay">
      <div className={`bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full ${getModalWidth()} border border-slate-200/60 dark:border-slate-700/60 animate-in zoom-in-95 duration-200`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200/60 dark:border-slate-700/60">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getIconBg()}`}>
              {getIcon()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {type === 'remove-from-ict' ? 'Review the details below carefully' : 'This action cannot be undone'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            {formatMessage()}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`flex items-center space-x-2 px-6 py-3 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 ${getConfirmButtonStyle()}`}
            >
              {!loading && getIcon()}
              <span>{getConfirmButtonText()}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
